import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import TopHeader from '../../components/TopHeader';
import { legalAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft, ShieldAlert, Clock, CheckCircle, AlertTriangle,
  User, FileText, Send, Loader2,
  ChevronDown, AlertCircle, Lock, Edit3,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Config (same as dashboard) ───────────────────────────────────────────────

const STATUS_FLOW = [
  { value: 'open',               label: 'Open',               color: 'bg-red-500',     text: 'text-red-700',    bg: 'bg-red-100' },
  { value: 'under_review',       label: 'Under Review',       color: 'bg-indigo-500',  text: 'text-indigo-700', bg: 'bg-indigo-100' },
  { value: 'awaiting_evidence',  label: 'Awaiting Evidence',  color: 'bg-amber-500',   text: 'text-amber-700',  bg: 'bg-amber-100' },
  { value: 'awaiting_client',    label: 'Awaiting Client',    color: 'bg-violet-500',  text: 'text-violet-700', bg: 'bg-violet-100' },
  { value: 'in_mediation',       label: 'In Mediation',       color: 'bg-sky-500',     text: 'text-sky-700',    bg: 'bg-sky-100' },
  { value: 'resolved',           label: 'Resolved',           color: 'bg-emerald-500', text: 'text-emerald-700',bg: 'bg-emerald-100' },
  { value: 'closed',             label: 'Closed',             color: 'bg-slate-400',   text: 'text-slate-600',  bg: 'bg-slate-100' },
  { value: 'escalated_to_court', label: 'Escalated to Court', color: 'bg-red-800',     text: 'text-red-900',    bg: 'bg-red-100' },
];

const URGENCY_CONFIG = {
  low:      { label: 'Low',      color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
  medium:   { label: 'Medium',   color: 'bg-blue-100 text-blue-700',   dot: 'bg-blue-500' },
  high:     { label: 'High',     color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700',     dot: 'bg-red-500' },
};

const TYPE_LABELS = {
  client_complaint: 'Client Complaint', document_dispute: 'Document Dispute',
  visa_rejection_dispute: 'Visa Rejection Dispute', fraud_concern: 'Fraud Concern',
  legal_threat: 'Legal Threat', regulatory_compliance: 'Regulatory Compliance',
  fee_refund_dispute: 'Fee / Refund Dispute', misrepresentation_claim: 'Misrepresentation Claim',
  privacy_data_concern: 'Privacy / Data Concern', other: 'Other',
};

const ROLE_LABELS = {
  client: 'Client', case_officer: 'Case Officer', sales_consultant: 'Sales Consultant',
  admin: 'Admin', super_admin: 'Super Admin', legal_counsel: 'Legal Counsel', process_manager: 'Process Manager',
};

function Field({ label, value, mono, large }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-sm text-slate-800 ${mono ? 'font-mono' : ''} ${large ? 'leading-relaxed' : 'font-medium'}`}>{value}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children, accent = 'border-slate-200' }) {
  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${accent}`}>
      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-slate-100 bg-slate-50">
        <Icon size={16} className="text-slate-500" />
        <p className="font-bold text-slate-800 text-sm">{title}</p>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  );
}

export default function LegalEscalationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [esc, setEsc]             = useState(null);
  const [notes, setNotes]         = useState([]);
  const [evidence, setEvidence]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'evidence' | 'timeline' | 'notes'

  // Status update
  const [showUpdate, setShowUpdate]     = useState(false);
  const [newStatus, setNewStatus]       = useState('');
  const [newAssignee, setNewAssignee]   = useState('');
  const [resolution, setResolution]     = useState('');
  const [comment, setComment]           = useState('');
  const [saving, setSaving]             = useState(false);
  const [legalUsers, setLegalUsers]     = useState([]);

  // Note
  const [noteText, setNoteText]         = useState('');
  const [addingNote, setAddingNote]     = useState(false);

  const isLegal = ['legal_counsel', 'super_admin', 'admin'].includes(user?.role);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [escRes, notesRes, evidenceRes] = await Promise.allSettled([
        legalAPI.getEscalation(id),
        legalAPI.getNotes(id),
        legalAPI.getEvidence(id),
      ]);
      if (escRes.status === 'fulfilled') { setEsc(escRes.value.data); setNewStatus(escRes.value.data.status); }
      if (notesRes.status === 'fulfilled') setNotes(notesRes.value.data);
      if (evidenceRes.status === 'fulfilled') setEvidence(evidenceRes.value.data);
    } catch { toast.error('Failed to load escalation'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await legalAPI.updateEscalation(id, {
        status: newStatus,
        assigned_to_id: newAssignee || null,
        resolution: resolution || null,
        internal_comment: comment || null,
      });
      toast.success('Escalation updated');
      setShowUpdate(false);
      setComment('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await legalAPI.addNote(id, { content: noteText.trim(), is_privileged: true });
      setNoteText('');
      fetchAll();
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
    finally { setAddingNote(false); }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 ml-[260px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
        </div>
      </div>
    );
  }

  if (!esc) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 ml-[260px] flex items-center justify-center">
          <p className="text-slate-400">Escalation not found</p>
        </div>
      </div>
    );
  }

  const urgencyCfg = URGENCY_CONFIG[esc.urgency] || URGENCY_CONFIG.low;
  const statusCfg  = STATUS_FLOW.find(s => s.value === esc.status) || STATUS_FLOW[0];
  const isResolved = ['resolved', 'closed'].includes(esc.status);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-[260px]">
        <TopHeader />
        <main className="flex-1 p-6 space-y-5 max-w-6xl">

          {/* ── Back + Header ── */}
          <div className="flex items-start gap-4">
            <button onClick={() => navigate('/legal')} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm mt-1">
              <ArrowLeft size={18} /> Back
            </button>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${urgencyCfg.color}`}>
                  <span className={`w-2 h-2 rounded-full ${urgencyCfg.dot}`} />
                  {urgencyCfg.label} Priority
                </span>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${statusCfg.bg} ${statusCfg.text}`}>
                  {statusCfg.label}
                </span>
                {esc.days_open > 7 && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-600 font-bold bg-red-100 px-2 py-0.5 rounded-full">
                    <AlertCircle size={11} /> {esc.days_open} days open
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-black text-slate-900">{esc.summary}</h1>
              <p className="text-sm text-slate-500 mt-1">
                {TYPE_LABELS[esc.escalation_type]} · {esc.client_name}{esc.ucin ? ` · ${esc.ucin}` : ''} · Raised by {esc.raised_by_name} ({ROLE_LABELS[esc.raised_by_role] || esc.raised_by_role})
              </p>
            </div>
          </div>

          {/* ── Status timeline ── */}
          <div className="bg-white rounded-2xl border border-slate-200 px-6 py-4 shadow-sm">
            <div className="flex items-center gap-2 overflow-x-auto">
              {STATUS_FLOW.slice(0, 6).map((s, i) => {
                const idx = STATUS_FLOW.findIndex(x => x.value === esc.status);
                const done = i < idx;
                const active = i === idx;
                return (
                  <React.Fragment key={s.value}>
                    <div className={`flex items-center gap-2 shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold ${active ? `${s.color} text-white` : done ? 'bg-slate-200 text-slate-600' : 'bg-slate-100 text-slate-400'}`}>
                      {done && <CheckCircle size={12} />}
                      {s.label}
                    </div>
                    {i < 5 && <div className={`w-5 h-px shrink-0 ${done || active ? 'bg-slate-400' : 'bg-slate-200'}`} />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* ── Tab Navigation ── */}
          <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 w-fit">
            {[
              { key: 'overview',  label: 'Overview' },
              { key: 'evidence',  label: evidence ? `Evidence Package (${(evidence.approved_documents?.length || 0) + (evidence.activity_log?.length || 0)} items)` : 'Evidence Package' },
              { key: 'timeline',  label: `Activity Log (${evidence?.activity_log?.length || 0})` },
              { key: 'notes',     label: `Notes (${notes.length})` },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === t.key ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Overview Tab ── */}
          {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">

              {/* ── Case / Client Info ── */}
              <Section title="Case & Client Information" icon={User}>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  <Field label="Client Name" value={esc.client_name} />
                  <Field label="Case Number (UCIN)" value={esc.ucin} mono />
                  <Field label="Visa Type" value={esc.visa_type} />
                  <Field label="Case Officer" value={esc.case_officer_name} />
                  <Field label="Incident Date" value={esc.incident_date} />
                  {esc.potential_liability_inr && (
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Estimated Liability</p>
                      <p className="text-sm font-bold text-red-700">INR {Number(esc.potential_liability_inr).toLocaleString('en-IN')}</p>
                    </div>
                  )}
                </div>
              </Section>

              {/* ── Detailed Description ── */}
              <Section title="Attorney Brief — Detailed Description" icon={FileText} accent="border-indigo-200">
                <div className="space-y-4">
                  <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
                    <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{esc.detailed_description}</p>
                  </div>
                  {esc.client_demands && (
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Client's Demands / Claims</p>
                      <p className="text-sm text-slate-700 bg-rose-50 border border-rose-100 rounded-xl p-3 whitespace-pre-wrap">{esc.client_demands}</p>
                    </div>
                  )}
                  {esc.prior_resolution_attempts && (
                    <Field label="Prior Resolution Attempts" value={esc.prior_resolution_attempts} large />
                  )}
                  {esc.witnesses && <Field label="Witnesses" value={esc.witnesses} />}
                </div>
              </Section>

              {/* ── Supporting Documents ── */}
              {esc.supporting_document_names?.length > 0 && (
                <Section title="Supporting Documents" icon={FileText}>
                  <div className="flex flex-wrap gap-2">
                    {esc.supporting_document_names.map((d, i) => (
                      <span key={i} className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold">{d}</span>
                    ))}
                  </div>
                  {esc.complete_file_attached && esc.complete_file_record && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-xs text-green-800">
                      <CheckCircle size={14} />
                      <span><strong>Complete File Approval Record attached</strong> — Approved {esc.complete_file_record.approved_at ? new Date(esc.complete_file_record.approved_at).toLocaleDateString('en-IN') : ''} · IP: {esc.complete_file_record.client_ip} · {esc.complete_file_record.total_documents} documents</span>
                    </div>
                  )}
                </Section>
              )}

              {/* ── Resolution (if resolved) ── */}
              {esc.resolution && (
                <Section title="Resolution" icon={CheckCircle} accent="border-emerald-200">
                  <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{esc.resolution}</p>
                    {esc.resolved_at && <p className="text-xs text-emerald-600 mt-2 font-medium">Resolved on {new Date(esc.resolved_at).toLocaleDateString('en-IN', { dateStyle: 'long' })}</p>}
                  </div>
                </Section>
              )}

              {/* ── Internal Notes ── */}
              <Section title="Internal Notes — Privileged & Confidential" icon={Lock} accent="border-amber-200">
                <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl mb-2">
                  <Lock size={13} className="text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-700">Notes in this section are visible only to legal counsel, admins, and super admins.</p>
                </div>

                {/* Notes list */}
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {notes.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>
                  ) : notes.map(n => (
                    <div key={n.id} className={`p-3 rounded-xl border text-sm ${n.is_status_change ? 'bg-slate-50 border-slate-200 text-slate-600 italic' : 'bg-white border-slate-200'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-700">{n.author_name}</span>
                        <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString('en-IN')}</span>
                      </div>
                      <p className={n.is_status_change ? 'text-slate-500' : 'text-slate-800'}>{n.content}</p>
                    </div>
                  ))}
                </div>

                {/* Add note */}
                {isLegal && (
                  <div className="flex gap-2 mt-3">
                    <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
                      placeholder="Add a privileged internal note…"
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddNote()}
                    />
                    <button onClick={handleAddNote} disabled={addingNote || !noteText.trim()}
                      className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 disabled:opacity-50">
                      {addingNote ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    </button>
                  </div>
                )}
              </Section>
            </div>

            {/* ── Right sidebar ── */}
            <div className="space-y-5">

              {/* Escalation meta */}
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
                <p className="font-bold text-slate-800 text-sm">Escalation Details</p>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Status</p>
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${statusCfg.bg} ${statusCfg.text}`}>{statusCfg.label}</span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Urgency</p>
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${urgencyCfg.color}`}>{urgencyCfg.label}</span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Assigned To</p>
                    <p className="text-sm font-semibold text-slate-700">{esc.assigned_to_name || <span className="text-slate-400 italic font-normal">Unassigned</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Raised By</p>
                    <p className="text-sm font-semibold text-slate-700">{esc.raised_by_name}</p>
                    <p className="text-xs text-slate-400">{ROLE_LABELS[esc.raised_by_role] || esc.raised_by_role}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Raised On</p>
                    <p className="text-sm font-semibold text-slate-700">{new Date(esc.created_at).toLocaleDateString('en-IN', { dateStyle: 'long' })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Days Open</p>
                    <p className={`text-lg font-black ${esc.days_open > 7 ? 'text-red-600' : 'text-slate-800'}`}>{esc.days_open}d</p>
                  </div>
                </div>
              </div>

              {/* ── Update Status Panel ── */}
              {isLegal && !isResolved && (
                <div className="bg-white rounded-2xl border border-indigo-200 shadow-sm overflow-hidden">
                  <button onClick={() => setShowUpdate(!showUpdate)} className="w-full flex items-center justify-between px-5 py-4 bg-indigo-50 hover:bg-indigo-100 transition-all">
                    <div className="flex items-center gap-2">
                      <Edit3 size={15} className="text-indigo-600" />
                      <p className="font-bold text-indigo-800 text-sm">Update Escalation</p>
                    </div>
                    <ChevronDown size={16} className={`text-indigo-500 transition-transform ${showUpdate ? 'rotate-180' : ''}`} />
                  </button>

                  {showUpdate && (
                    <div className="p-5 space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase tracking-wide">New Status</label>
                        <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
                          {STATUS_FLOW.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      {(newStatus === 'resolved' || newStatus === 'closed') && (
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1 uppercase tracking-wide">Resolution Notes *</label>
                          <textarea rows={3} value={resolution} onChange={e => setResolution(e.target.value)}
                            placeholder="Describe how this was resolved…"
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none" />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase tracking-wide">Internal Comment</label>
                        <input type="text" value={comment} onChange={e => setComment(e.target.value)}
                          placeholder="Optional note about this update"
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                      </div>
                      <button onClick={handleUpdate} disabled={saving || ((newStatus === 'resolved' || newStatus === 'closed') && !resolution.trim())}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 disabled:opacity-50">
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                        Save Update
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )} {/* end overview tab */}

          {/* ── Evidence Package Tab ── */}
          {activeTab === 'evidence' && (
            <div className="space-y-5">
              {!evidence ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                  <AlertCircle size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 font-medium">No evidence package available</p>
                  <p className="text-xs text-slate-400 mt-1">This escalation was raised before automatic evidence capture was implemented</p>
                </div>
              ) : (
                <>
                  {/* Evidence header */}
                  <div className="bg-gradient-to-r from-indigo-700 to-purple-700 rounded-2xl p-5 text-white">
                    <div className="flex items-center gap-3 mb-2">
                      <Lock size={20} className="text-white/80" />
                      <p className="font-bold text-lg">Court-Ready Evidence Package</p>
                    </div>
                    <p className="text-indigo-200 text-sm">Immutable snapshot captured automatically at escalation creation · {new Date(evidence.snapshot_taken_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'full', timeStyle: 'medium' })}</p>
                    <p className="text-indigo-300 text-xs mt-1">Captured by: {evidence.snapshot_taken_by}</p>
                  </div>

                  {/* Client Profile */}
                  {evidence.client_snapshot && (
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-slate-100 bg-slate-50">
                        <User size={16} className="text-slate-500" />
                        <p className="font-bold text-slate-800 text-sm">Client Profile (Snapshot)</p>
                      </div>
                      <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3">
                        {[
                          ['Full Name', evidence.client_snapshot.full_name],
                          ['Email Address', evidence.client_snapshot.email],
                          ['Phone', evidence.client_snapshot.phone],
                          ['Passport Number', evidence.client_snapshot.passport_number],
                          ['Nationality', evidence.client_snapshot.nationality],
                          ['Visa Type', evidence.client_snapshot.visa_type],
                          ['Payment Status', evidence.client_snapshot.payment_status],
                          ['Service Fee', evidence.client_snapshot.payment_amount ? `INR ${Number(evidence.client_snapshot.payment_amount).toLocaleString('en-IN')}` : null],
                          ['Sales Consultant', evidence.client_snapshot.sales_consultant],
                          ['Client Since', evidence.client_snapshot.created_at ? new Date(evidence.client_snapshot.created_at).toLocaleDateString('en-IN', { dateStyle: 'long' }) : null],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label}>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
                            <p className="text-sm font-semibold text-slate-800">{value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Case Snapshot */}
                  {evidence.case_snapshot && (
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-slate-100 bg-slate-50">
                        <FileText size={16} className="text-slate-500" />
                        <p className="font-bold text-slate-800 text-sm">Case Details (Snapshot)</p>
                      </div>
                      <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3">
                        {[
                          ['Case Number (UCIN)', evidence.case_snapshot.ucin],
                          ['Visa Program', evidence.case_snapshot.visa_program],
                          ['Case Stage', evidence.case_snapshot.stage],
                          ['Process Stage', evidence.case_snapshot.current_process_stage],
                          ['Assigned Officer', evidence.case_snapshot.assigned_officer],
                          ['Agreement Signed', evidence.case_snapshot.agreement_signed ? `Yes — ${evidence.case_snapshot.agreement_signed_at ? new Date(evidence.case_snapshot.agreement_signed_at).toLocaleDateString('en-IN') : ''}` : 'Not signed'],
                          ['Documents Completed', evidence.case_snapshot.documents_completed ? 'Yes' : 'No'],
                          ['Visa Decision', evidence.case_snapshot.visa_decision || 'Pending'],
                          ['Payment Status', evidence.case_snapshot.payment_status],
                          ['Service Start', evidence.case_snapshot.service_start_date ? new Date(evidence.case_snapshot.service_start_date).toLocaleDateString('en-IN') : null],
                          ['Service End', evidence.case_snapshot.service_end_date ? new Date(evidence.case_snapshot.service_end_date).toLocaleDateString('en-IN') : null],
                          ['Case Created', evidence.case_snapshot.created_at ? new Date(evidence.case_snapshot.created_at).toLocaleDateString('en-IN', { dateStyle: 'long' }) : null],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label}>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
                            <p className="text-sm font-semibold text-slate-800">{value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Complete File Approval — the most important legal evidence */}
                  {evidence.complete_file_approval ? (
                    <div className="bg-white rounded-2xl border-2 border-green-400 overflow-hidden shadow-sm">
                      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-green-200 bg-green-50">
                        <CheckCircle size={16} className="text-green-600" />
                        <p className="font-bold text-green-800 text-sm">Complete File Approval — Signed by Client</p>
                        <span className="ml-auto text-xs bg-green-600 text-white px-2 py-0.5 rounded-full font-bold">LEGALLY BINDING</span>
                      </div>
                      <div className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                          <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Client Name</p><p className="text-sm font-semibold">{evidence.complete_file_approval.client_name}</p></div>
                          <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Client Email</p><p className="text-sm font-semibold">{evidence.complete_file_approval.client_email}</p></div>
                          <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Date & Time (IST)</p><p className="text-sm font-semibold">{evidence.complete_file_approval.approved_at_utc ? new Date(evidence.complete_file_approval.approved_at_utc).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'full', timeStyle: 'long' }) : '—'}</p></div>
                          <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Date & Time (UTC)</p><p className="text-sm font-semibold">{evidence.complete_file_approval.approved_at_utc ? new Date(evidence.complete_file_approval.approved_at_utc).toUTCString() : '—'}</p></div>
                          <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">IP Address</p><p className="text-sm font-semibold font-mono text-indigo-700">{evidence.complete_file_approval.client_ip_address}</p></div>
                          <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Documents</p><p className="text-sm font-semibold">{evidence.complete_file_approval.total_documents_acknowledged} acknowledged</p></div>
                          <div className="col-span-2"><p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Browser / Device</p><p className="text-xs text-slate-600 font-mono bg-slate-50 p-2 rounded-lg break-all">{evidence.complete_file_approval.client_user_agent}</p></div>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Signed Acknowledgements</p>
                          <div className="space-y-2">
                            {[evidence.complete_file_approval.acknowledgement_1, evidence.complete_file_approval.acknowledgement_2, evidence.complete_file_approval.acknowledgement_3].filter(Boolean).map((ack, i) => (
                              <div key={i} className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                                <CheckCircle size={14} className="text-green-600 mt-0.5 shrink-0" />
                                <p className="text-xs text-slate-700">{ack}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Document list */}
                        {(evidence.complete_file_approval.client_documents?.length > 0 || evidence.complete_file_approval.officer_documents?.length > 0 || evidence.complete_file_approval.government_forms?.length > 0) && (
                          <div>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Documents Included in Approved File</p>
                            <div className="space-y-1">
                              {evidence.complete_file_approval.client_documents?.map((d, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100">
                                  <span className="w-5 h-5 bg-teal-100 text-teal-700 rounded text-center font-bold">{i+1}</span>
                                  <span className="text-slate-700 flex-1">{d.checklist_item_name}{d.document_side_label ? ` — ${d.document_side_label}` : ''}</span>
                                  <span className="text-slate-400">Client uploaded</span>
                                </div>
                              ))}
                              {evidence.complete_file_approval.officer_documents?.map((d, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100">
                                  <span className="w-5 h-5 bg-indigo-100 text-indigo-700 rounded text-center font-bold">{(evidence.complete_file_approval.client_documents?.length || 0)+i+1}</span>
                                  <span className="text-slate-700 flex-1">{d.checklist_item_name}</span>
                                  <span className="text-slate-400">Officer prepared</span>
                                </div>
                              ))}
                              {evidence.complete_file_approval.government_forms?.map((f, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100">
                                  <span className="w-5 h-5 bg-amber-100 text-amber-700 rounded text-center font-bold">{(evidence.complete_file_approval.client_documents?.length || 0)+(evidence.complete_file_approval.officer_documents?.length || 0)+i+1}</span>
                                  <span className="text-slate-700 flex-1">{f.name}</span>
                                  <span className="text-slate-400">Government form</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-3">
                      <AlertTriangle size={20} className="text-amber-500 shrink-0" />
                      <p className="text-sm text-amber-800">No Complete File approval record. Client had not approved their complete file at the time of escalation.</p>
                    </div>
                  )}

                  {/* Approved Documents */}
                  {evidence.approved_documents?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-slate-100 bg-slate-50">
                        <CheckCircle size={16} className="text-green-500" />
                        <p className="font-bold text-slate-800 text-sm">Approved Documents ({evidence.approved_documents.length})</p>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {evidence.approved_documents.map((d, i) => (
                          <div key={i} className="flex items-center justify-between px-6 py-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{d.name}{d.side_label ? ` — ${d.side_label}` : ''}</p>
                              {d.applicant_name && <p className="text-xs text-slate-400">For: {d.applicant_name}</p>}
                            </div>
                            <div className="text-right text-xs text-slate-500">
                              <p>Uploaded: {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('en-IN') : '—'}</p>
                              <p>Approved: {d.approved_at ? new Date(d.approved_at).toLocaleDateString('en-IN') : '—'} by {d.approved_by_role}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Rejected Documents */}
                  {evidence.rejected_documents?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-red-200 overflow-hidden shadow-sm">
                      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-red-100 bg-red-50">
                        <AlertCircle size={16} className="text-red-500" />
                        <p className="font-bold text-red-800 text-sm">Rejected Documents ({evidence.rejected_documents.length})</p>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {evidence.rejected_documents.map((d, i) => (
                          <div key={i} className="px-6 py-3">
                            <p className="text-sm font-semibold text-slate-800">{d.name}</p>
                            {d.reviewer_notes && <p className="text-xs text-red-600 mt-0.5">Officer note: {d.reviewer_notes}</p>}
                            {d.client_notes && <p className="text-xs text-amber-600 mt-0.5">Client note: {d.client_notes}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Previous Complaints */}
                  {evidence.complaints?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-orange-200 overflow-hidden shadow-sm">
                      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-orange-100 bg-orange-50">
                        <AlertTriangle size={16} className="text-orange-500" />
                        <p className="font-bold text-orange-800 text-sm">Previous Complaints ({evidence.complaints.length})</p>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {evidence.complaints.map((c, i) => (
                          <div key={i} className="px-6 py-3">
                            <p className="text-sm font-semibold text-slate-800">{c.subject}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-slate-500">{c.complaint_type}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{c.status}</span>
                              <span className="text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : ''}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Activity Timeline Tab ── */}
          {activeTab === 'timeline' && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="flex items-center gap-2.5 px-6 py-4 border-b border-slate-100 bg-slate-50">
                <AlertCircle size={16} className="text-slate-500" />
                <p className="font-bold text-slate-800 text-sm">Full Case Activity Log — {evidence?.activity_log?.length || 0} entries (captured at escalation time)</p>
              </div>
              {!evidence?.activity_log?.length ? (
                <div className="text-center py-12 text-slate-400 text-sm">No activity log available</div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {evidence.activity_log.map((log, i) => (
                    <div key={i} className="flex items-start gap-4 px-6 py-3">
                      <div className="w-2 h-2 rounded-full bg-slate-400 mt-2 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800">{log.description}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-slate-500">{log.user_name}</span>
                          <span className="text-xs text-indigo-600 font-mono">{log.activity_type}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-slate-500 whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p>
                        <p className="text-xs text-slate-400">{log.timestamp ? new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Notes Tab ── */}
          {activeTab === 'notes' && (
            <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden shadow-sm">
              <div className="flex items-center gap-2.5 px-6 py-4 border-b border-amber-100 bg-amber-50">
                <Lock size={16} className="text-amber-600" />
                <p className="font-bold text-amber-800 text-sm">Internal Privileged Notes — Confidential</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                  Notes here are visible only to legal counsel, admins, and super admins. These are privileged communications.
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {notes.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No notes yet</p>
                    : notes.map(n => (
                    <div key={n.id} className={`p-3 rounded-xl border text-sm ${n.is_status_change ? 'bg-slate-50 border-slate-200 italic text-slate-500' : 'bg-white border-slate-200'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-700">{n.author_name}</span>
                        <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString('en-IN')}</span>
                      </div>
                      <p>{n.content}</p>
                    </div>
                  ))}
                </div>
                {isLegal && (
                  <div className="flex gap-2 pt-2 border-t border-slate-200">
                    <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
                      placeholder="Add a privileged internal note…"
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddNote()} />
                    <button onClick={handleAddNote} disabled={addingNote || !noteText.trim()}
                      className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 disabled:opacity-50">
                      {addingNote ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
